import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function InstitutionDashboard() {
  const [token, setToken] = useState(localStorage.getItem("msis_inst_token") || "");
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (token) {
      api.get("/institution/dashboard", { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setData(r.data))
        .catch(() => { localStorage.removeItem("msis_inst_token"); setToken(""); });
    }
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/institution/login", { email, password });
      localStorage.setItem("msis_inst_token", r.data.token);
      setToken(r.data.token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
  };
  const logout = () => { localStorage.removeItem("msis_inst_token"); setToken(""); setData(null); };

  if (!token) {
    return (
      <Layout>
        <section className="container-msis py-20 max-w-md">
          <div className="overline mb-3">Institution Portal</div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Sign in</h1>
          <form onSubmit={login} className="mt-8 space-y-4" data-testid="inst-login-form">
            <div><Label>Email</Label><Input type="email" data-testid="inst-email" value={email} onChange={e => setEmail(e.target.value)} required /></div>
            <div><Label>Password</Label><Input type="password" data-testid="inst-password" value={password} onChange={e => setPassword(e.target.value)} required /></div>
            <button className="btn-primary w-full" data-testid="inst-login-btn">Sign In</button>
            <p className="text-xs text-slate2">Don't have credentials yet? They are emailed after you complete an institutional subscription.</p>
          </form>
        </section>
      </Layout>
    );
  }

  if (!data) return <Layout><div className="container-msis py-20">Loading…</div></Layout>;

  const stats = data.stats;
  const tabs = [["overview", "Overview"], ["test_ids", "Test IDs"], ["reports", "Reports"], ["analytics", "Analytics"]];

  return (
    <Layout>
      <section className="container-msis py-10" data-testid="inst-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-6">
          <div>
            <div className="overline">{data.institution.name}</div>
            <h1 className="font-display font-bold text-3xl tracking-tight mt-1">Institution Dashboard</h1>
            <div className="text-sm text-slate2 mt-1">Institution Code: <span className="font-mono text-ink">{data.institution.code}</span></div>
          </div>
          <button onClick={logout} className="btn-ghost" data-testid="inst-logout">Sign out</button>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border mt-8">
          {[["purchased", "Purchased"], ["unused", "Unused"], ["verified", "Verified"], ["started", "Started"], ["completed", "Completed"]].map(([k, l]) => (
            <div key={k} className="bg-white p-5">
              <div className="overline">{l}</div>
              <div className="font-display font-bold text-2xl mt-2 tabular-nums">{stats[k] || 0}</div>
            </div>
          ))}
        </div>

        <div className="mt-8 border-b border-border flex gap-6">
          {tabs.map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`pb-3 text-sm transition-colors ${tab === k ? "text-navy border-b-2 border-navy font-medium" : "text-slate2"}`}
              data-testid={`inst-tab-${k}`}>{l}</button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "overview" && (
            <div className="text-sm text-slate2">Welcome. Use the tabs above to manage your Test IDs, view completed reports, and view aggregate analytics for your batch.</div>
          )}
          {tab === "test_ids" && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm" data-testid="testids-table">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-3">Test ID</th><th className="text-left p-3">Status</th><th className="text-left p-3">Email</th><th className="text-left p-3">Created</th></tr>
                </thead>
                <tbody>
                  {(data.test_ids || []).map((t) => (
                    <tr key={t.test_id} className="border-t border-border">
                      <td className="p-3 font-mono">{t.test_id}</td>
                      <td className="p-3"><span className={`text-xs px-2 py-1 rounded ${t.status === 'completed' ? 'bg-green-50 text-green-700' : t.status === 'unused' ? 'bg-subtle text-slate2' : 'bg-blue-50 text-blue-700'}`}>{t.status}</span></td>
                      <td className="p-3">{t.locked_email || '—'}</td>
                      <td className="p-3 text-slate2">{(t.created_at || '').split('T')[0]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "reports" && (
            <div className="border border-border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-subtle text-slate2 text-xs uppercase">
                  <tr><th className="text-left p-3">Student</th><th className="text-left p-3">Test ID</th><th className="text-left p-3">Top Domain</th><th className="text-right p-3">Score</th></tr>
                </thead>
                <tbody>
                  {(data.reports || []).map((r) => (
                    <tr key={r.id} className="border-t border-border">
                      <td className="p-3">{r.name}<div className="text-xs text-slate2">{r.email}</div></td>
                      <td className="p-3 font-mono text-xs">{r.test_id}</td>
                      <td className="p-3">{r.top_domain}</td>
                      <td className="p-3 text-right font-mono">{r.top_score?.toFixed?.(2) || r.top_score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {tab === "analytics" && (
            <div className="grid sm:grid-cols-2 gap-px bg-border">
              {(data.aggregate_domains || []).map((d) => (
                <div key={d.code} className="bg-white p-5">
                  <div className="flex items-center justify-between">
                    <div className="font-mono text-xs text-slate2">{d.code}</div>
                    <div className="font-mono text-sm">{d.avg_score.toFixed(2)} avg · n={d.n}</div>
                  </div>
                  <div className="font-display font-semibold mt-2">{d.name}</div>
                  <div className="h-2 bg-subtle mt-3 rounded"><div className="h-full bg-navy rounded" style={{ width: `${(d.avg_score / 5) * 100}%` }} /></div>
                </div>
              ))}
              {(data.aggregate_domains || []).length === 0 && <div className="bg-white p-5 text-slate2 text-sm">No completed reports yet.</div>}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}
